import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { ImagePlus, Loader2, X } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import { apiFetch } from '@/api/apiFetch';
import { toastApiError } from '@/lib/query-client';
import { useApp } from '@/components/hooks/useApp';
import { qk } from '@/lib/qk';
import { captureUserBugReport } from '@/lib/sentryInit';

// Duplicadas y no importadas de `components/ai/v2/imageAttachment`: la regla
// `nadie-depende-de-ai` de dependency-cruiser existe para que ARIA siga siendo
// extraíble, y su propio comentario dice qué hacer con esto — «lo específico se
// duplica antes que acoplarse». Aquí, además, el uso es distinto: ARIA convierte
// la imagen a base64 para el modelo; soporte sube el fichero tal cual a S3.
const TIPOS_ADMITIDOS = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
const MAX_BYTES = 15 * 1024 * 1024;

const esImagenAdmitida = (file) => !!file && TIPOS_ADMITIDOS.includes(file.type);

const EMPTY = {
  title: '', description: '', pasos: '', esperado: '',
  category: 'consulta', severity: 'medium',
};

const SEVERITIES = [
  { value: 'low', label: 'Baja — puedo seguir trabajando' },
  { value: 'medium', label: 'Media — me entorpece' },
  { value: 'high', label: 'Alta — no puedo hacer mi tarea' },
  { value: 'critical', label: 'Crítica — la empresa está parada' },
];

/**
 * Compone el cuerpo del reporte. Los tres campos van juntos a `description`
 * (una sola columna en `bug_reports`) porque separarlos en la tabla obligaría a
 * migrar el modelo para algo que solo se lee como texto corrido en el panel.
 */
function componerDescripcion({ description, pasos, esperado }) {
  const bloques = [description.trim()];
  if (pasos.trim()) bloques.push(`Pasos para reproducirlo:\n${pasos.trim()}`);
  if (esperado.trim()) bloques.push(`Esperaba que:\n${esperado.trim()}`);
  return bloques.join('\n\n');
}

// In-app bug report form. Lands the report in our own `bug_reports` table
// (POST /api/bug-reports) AND emits a linked Sentry event (event id stored in
// the row) so the report can be cross-referenced from Sentry.
// company_id / user_id are derived server-side — never sent from here.
export default function BugReportDialog({ open, onOpenChange, user }) {
  const { company } = useApp();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(EMPTY);
  const [captura, setCaptura] = useState(null); // { file, previewUrl, name }
  const [subiendo, setSubiendo] = useState(false);
  const inputFileRef = useRef(null);

  const set = (campo) => (e) => setForm((f) => ({ ...f, [campo]: e.target.value }));

  const limpiar = () => {
    setForm(EMPTY);
    setCaptura((actual) => {
      if (actual?.previewUrl) URL.revokeObjectURL(actual.previewUrl);
      return null;
    });
  };

  const mutation = useMutation({
    mutationFn: (payload) => apiFetch('/api/bug-reports', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
    onSuccess: () => {
      toast.success('Incidencia enviada al equipo de VERIX', {
        description: 'Puedes seguir su estado en Soporte.',
      });
      // Sin esto, el enlace "Ver mis incidencias" de este mismo diálogo lleva a
      // una lista que no incluye lo que se acaba de enviar: la query tiene
      // staleTime de 60 s y serviría la caché anterior.
      queryClient.invalidateQueries({ queryKey: qk.bugReport.list(company?.id) });
      limpiar();
      onOpenChange(false);
    },
    onError: (error) => toastApiError(error, 'No se pudo enviar el reporte'),
  });

  const elegirCaptura = (file) => {
    if (!file) return;
    if (!esImagenAdmitida(file)) {
      toast.error('Formato no admitido. Usa JPG, PNG, WebP o GIF.');
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error('La imagen es demasiado grande (máx. 15 MB).');
      return;
    }
    setCaptura((actual) => {
      if (actual?.previewUrl) URL.revokeObjectURL(actual.previewUrl);
      return { file, previewUrl: URL.createObjectURL(file), name: file.name };
    });
  };

  const quitarCaptura = () => {
    setCaptura((actual) => {
      if (actual?.previewUrl) URL.revokeObjectURL(actual.previewUrl);
      return null;
    });
    if (inputFileRef.current) inputFileRef.current.value = '';
  };

  /**
   * Sube la captura a S3 por el camino canónico (presigned → PUT). El scope
   * `bug_report` es el que fija el prefijo `bug-reports/<company>/`, que es lo
   * único que el backend acepta guardar en `screenshot_s3_key`.
   * Devuelve la key, o null si falla: una captura que no sube no debe impedir
   * que el reporte se envíe.
   */
  const subirCaptura = async () => {
    if (!captura) return null;
    try {
      const presigned = await apiFetch('/api/functions/presigned-upload', {
        method: 'POST',
        body: JSON.stringify({
          file_name: captura.file.name,
          file_type: captura.file.type,
          scope: 'bug_report',
        }),
      });
      if (!presigned?.uploadUrl || !presigned?.key) return null;

      const put = await fetch(presigned.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': captura.file.type },
        body: captura.file,
      });
      if (!put.ok) return null;
      return presigned.key;
    } catch {
      return null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.description.trim()) return;

    setSubiendo(true);
    let screenshotKey = null;
    try {
      screenshotKey = await subirCaptura();
      if (captura && !screenshotKey) {
        toast.warning('No se pudo adjuntar la captura; enviamos el reporte sin ella.');
      }
    } finally {
      setSubiendo(false);
    }

    const url = typeof window !== 'undefined' ? window.location.href : undefined;
    const descripcion = componerDescripcion(form);

    // Sentry queda como referencia cruzada (el detalle del reporte viaja ahora
    // por correo al equipo, no por aquí). Solo identificadores, nunca PII.
    const sentryEventId = captureUserBugReport(form.title, {
      user_id: user?.id,
      company_id: user?.company_id,
      role: user?.role,
      severity: form.severity,
      release: import.meta.env.VITE_RELEASE_SHA,
      url,
    });

    mutation.mutate({
      title: form.title.trim(),
      description: descripcion,
      category: form.category,
      severity: form.severity,
      url,
      ...(sentryEventId ? { sentry_event_id: sentryEventId } : {}),
      ...(screenshotKey ? { screenshot_s3_key: screenshotKey } : {}),
    });
  };

  const ocupado = subiendo || mutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Soporte</DialogTitle>
          <DialogDescription>
            Cuéntanos tu incidencia o consulta. Adjuntamos automáticamente la página en la
            que estás y los datos de tu navegador.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="bug-title">Asunto</Label>
            <Input
              id="bug-title"
              value={form.title}
              onChange={set('title')}
              placeholder="¿En qué podemos ayudarte?"
              maxLength={255}
              required
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="bug-description">Qué ha pasado</Label>
            <Textarea
              id="bug-description"
              value={form.description}
              onChange={set('description')}
              placeholder="Cuéntanos los detalles de tu incidencia o consulta."
              rows={3}
              maxLength={3000}
              required
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="bug-pasos">
                Pasos para reproducirlo <span className="text-muted-foreground font-normal">(opcional)</span>
              </Label>
              <Textarea
                id="bug-pasos"
                value={form.pasos}
                onChange={set('pasos')}
                placeholder="1. Abro Facturas&#10;2. Pulso Guardar"
                rows={3}
                maxLength={1000}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="bug-esperado">
                Qué esperabas <span className="text-muted-foreground font-normal">(opcional)</span>
              </Label>
              <Textarea
                id="bug-esperado"
                value={form.esperado}
                onChange={set('esperado')}
                placeholder="Que la factura se guardara y volviera al listado."
                rows={3}
                maxLength={1000}
              />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="bug-category">Categoría</Label>
              <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
                <SelectTrigger id="bug-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="incidencia">Incidencia técnica</SelectItem>
                  <SelectItem value="consulta">Consulta / Duda</SelectItem>
                  <SelectItem value="sugerencia">Sugerencia</SelectItem>
                  <SelectItem value="facturacion">Facturación</SelectItem>
                  <SelectItem value="otro">Otro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="bug-severity">Urgencia</Label>
              <Select value={form.severity} onValueChange={(v) => setForm((f) => ({ ...f, severity: v }))}>
                <SelectTrigger id="bug-severity">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SEVERITIES.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="bug-captura">
              Captura de pantalla <span className="text-muted-foreground font-normal">(opcional)</span>
            </Label>
            <input
              ref={inputFileRef}
              id="bug-captura"
              type="file"
              accept={TIPOS_ADMITIDOS.join(',')}
              className="hidden"
              onChange={(e) => { elegirCaptura(e.target.files?.[0]); e.target.value = ''; }}
            />
            {captura ? (
              <div className="flex items-center gap-3 rounded-lg border border-border p-2">
                <img
                  src={captura.previewUrl}
                  alt={captura.name}
                  className="h-14 w-14 rounded-md border border-border object-cover"
                />
                <span className="min-w-0 flex-1 truncate text-sm text-muted-foreground">{captura.name}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={quitarCaptura}
                  aria-label="Quitar captura"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <Button
                type="button"
                variant="outline"
                className="w-full justify-start"
                onClick={() => inputFileRef.current?.click()}
              >
                <ImagePlus className="mr-2 h-4 w-4" />
                Adjuntar una captura
              </Button>
            )}
          </div>

          <DialogFooter className="gap-2 sm:justify-between">
            <Button asChild variant="link" className="px-0 text-muted-foreground">
              <Link to="/soporte" onClick={() => onOpenChange(false)}>Ver mis incidencias</Link>
            </Button>
            <div className="flex gap-2">
              <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={ocupado}>
                {ocupado && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {subiendo ? 'Adjuntando…' : mutation.isPending ? 'Enviando…' : 'Enviar'}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
